import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCircleCheck, faPaintbrush, faHeadphones, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import AchievementBadge from "../Achievements/AchievementBadge";
import ProgressRing from "../ProgressRing/ProgressRing";
import { ACHIEVEMENTS } from "../../utils/achievements";
import { computeMissionProgress, MissionProgress } from "../../utils/missions";
import { ProgressSnapshots } from "../../utils/xp";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { useProgressText } from "../../i18n/progressText";
import styles from "./homePage.module.scss";

// Missions shown before the rest are folded behind the "Show more" button.
const VISIBLE_MISSIONS = 2;

function MissionItem({ item }: { item: MissionProgress }) {
    const { t } = useTranslation("homePage");
    const lp = useLocalizedPath();
    const { missionTitle, missionDescription, missionCta, achievementTitle } = useProgressText();
    const { mission, done, total, percent, completed, next } = item;
    const achievement = ACHIEVEMENTS.find((candidate) => candidate.id === mission.achievementId);
    const title = missionTitle(mission);
    const unit = t(`missions.units.${mission.unit}`, { count: total });
    const progressValues = { done, total, unit };

    let visual: ReactNode;
    let status: ReactNode;
    let action: ReactNode;

    if (completed) {
        const earnedTitle = achievement ? achievementTitle(achievement) : title;
        visual = achievement ? (
            <AchievementBadge achievement={achievement} size={56} />
        ) : (
            <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
        );
        status = (
            <>
                <p className={styles.missionCompletedLabel}>
                    <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
                    {t("missions.completed")}
                </p>
                <p className={styles.missionNext}>{t("missions.achievementEarned", { title: earnedTitle })}</p>
            </>
        );
        // Links to the profile, where the achievement's badge is listed. The
        // name carries the achievement so two completed missions don't expose
        // two identical "View achievement" links.
        action = (
            <Link
                to={lp("/profile")}
                className={`${styles.missionCta} ${styles.missionCtaSecondary}`}
                aria-label={`${t("missions.viewAchievement")}: ${earnedTitle}`}
            >
                {t("missions.viewAchievement")}
            </Link>
        );
    } else {
        visual = (
            <ProgressRing
                percent={percent}
                label={t("missions.progressLabel", { title })}
                valueText={t("missions.progressText", progressValues)}
            />
        );
        status = next ? (
            <p className={styles.missionNext}>
                {t("missions.nextUp")}
                <span className={styles.missionNextItem} lang="ja">
                    {next}
                </span>
            </p>
        ) : null;
        action = (
            <Link to={lp(mission.practicePath)} className={`${styles.missionCta} ${styles.missionCtaPrimary}`}>
                {missionCta(mission)}
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
        );
    }

    return (
        <li className={completed ? `${styles.missionItem} ${styles.missionItemCompleted}` : styles.missionItem}>
            {mission.kind === "draw" && (
                <span className={styles.missionKindIcon} data-testid="mission-kind-draw" aria-hidden="true">
                    <FontAwesomeIcon icon={faPaintbrush} />
                </span>
            )}
            {mission.kind === "listen" && (
                <span className={styles.missionKindIcon} data-testid="mission-kind-listen" aria-hidden="true">
                    <FontAwesomeIcon icon={faHeadphones} />
                </span>
            )}
            <div className={styles.missionVisual}>{visual}</div>
            <div className={styles.missionBody}>
                <h3 className={styles.missionTitle}>{title}</h3>
                <p className={styles.missionDescription}>{missionDescription(mission)}</p>
                <p className={styles.missionProgressText}>{t("missions.progress", progressValues)}</p>
                {status}
            </div>
            <div className={styles.missionAction}>{action}</div>
        </li>
    );
}

type MissionsCardProps = {
    // The per-feature progress snapshots the missions are measured against
    // (kana/kanji draw and listen counts). The caller owns loading them and must only
    // render this once they have resolved - see HomePage's isReady gate - so
    // the card never shows a partial, about-to-change state.
    snapshots: ProgressSnapshots;
    // The progress couldn't be loaded. Show that instead of an all-zero board,
    // which would wrongly tell a learner their finished missions are undone.
    loadFailed?: boolean;
};

// The "Missions" card on the welcome-back board: one row per mission with its
// progress, and either a call to action or - once completed - the achievement
// it unlocked.
function MissionsCard({ snapshots, loadFailed = false }: MissionsCardProps) {
    const { t } = useTranslation("homePage");
    const progress = loadFailed ? [] : computeMissionProgress(snapshots);
    const completedCount = progress.filter((item) => item.completed).length;
    const [expanded, setExpanded] = useState(false);
    const hiddenCount = Math.max(0, progress.length - VISIBLE_MISSIONS);
    const visible = expanded ? progress : progress.slice(0, VISIBLE_MISSIONS);

    return (
        <section className={styles.missionsSection} aria-labelledby="missions-heading">
            <div className={styles.missionsCard}>
                <div className={styles.missionsHeader}>
                    <h2 id="missions-heading" className={styles.missionsHeading}>
                        {t("missions.heading")}
                    </h2>
                    {!loadFailed && (
                        <p className={styles.missionsSummary}>
                            {t("missions.summary", { completed: completedCount, total: progress.length })}
                        </p>
                    )}
                    <p className={styles.missionsIntro}>{t("missions.intro")}</p>
                </div>
                {loadFailed ? (
                    <p className={styles.missionsError} role="alert">
                        {t("missions.error")}
                    </p>
                ) : (
                    <>
                        <ul className={styles.missionList} id="missions-list">
                            {visible.map((item) => (
                                <MissionItem key={item.mission.id} item={item} />
                            ))}
                        </ul>
                        {hiddenCount > 0 && (
                            <button
                                type="button"
                                className={styles.missionsToggle}
                                aria-expanded={expanded}
                                aria-controls="missions-list"
                                onClick={() => setExpanded((value) => !value)}
                            >
                                {expanded ? t("missions.showLess") : t("missions.showMore", { count: hiddenCount })}
                                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} aria-hidden="true" />
                            </button>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

export default MissionsCard;
